import React from "react";
import { Hero } from "../components/hero";
import { Section } from "../components/section";
import { Projects } from "../components/projects";
import { Skills } from "../components/skills";
import { Experience } from "../components/experience";
import { ContactForm } from "../components/contact-form";
import { Testimonials } from "../components/testimonials";
import { SEO, StructuredData } from "../components/seo";
import { motion } from "motion/react";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import { ArrowUpRight, Sparkles, Calendar, Clock, ArrowRight } from "lucide-react";
import { Map } from "../components/map";
import { Link } from "react-router";
import { posts } from "./blog";
import { WhyChooseUs } from "../components/why-choose-us";
import { FAQ } from "../components/faq";
import { TechMarquee } from "../components/tech-marquee";
import { Newsletter } from "../components/newsletter";

export function Home() {
  return (
    <>
      <SEO />
      <StructuredData />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="space-y-12 relative"
      >
        <div className="fixed top-[-10%] right-[-10%] -z-10 w-[80vw] h-[80vw] bg-brand-primary/15 blur-[150px] rounded-full animate-pulse pointer-events-none" />
        <div className="fixed top-[30%] left-[-20%] -z-10 w-[70vw] h-[70vw] bg-brand-secondary/15 blur-[130px] rounded-full animate-pulse delay-700 pointer-events-none" />
        <div className="fixed bottom-[-10%] right-[10%] -z-10 w-[60vw] h-[60vw] bg-brand-accent/15 blur-[180px] rounded-full animate-pulse delay-1000 pointer-events-none" />
        
        <div className="relative z-10 px-6 sm:px-10 lg:px-0">
          <Hero />
        </div>

        <TechMarquee />

        <Section 
          id="about" 
          title="Who I Am" 
          subtitle="A full-stack engineer dedicated to crafting seamless digital experiences with modern technology."
          dark
        >
          <div className="grid lg:grid-cols-2 gap-24 items-start">
            <div className="space-y-10">
              <div className="space-y-6">
                <p className="text-muted-foreground text-xl leading-relaxed font-medium">
                  I translate requirements into <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-secondary font-black">clean UI</span>, reliable backend logic, and well-structured APIs/data
                  flows—then deploy and support projects end-to-end.
                </p>
                <p className="text-muted-foreground/80 text-lg leading-relaxed font-medium">
                  My expertise spans modern JavaScript ecosystems (React, Next.js, Node.js) and established backend technologies like PHP and MySQL, 
                  allowing me to deliver robust business solutions ranging from admin dashboards to multilingual reservation systems.
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 pt-12 border-t border-border">
                <div className="group">
                  <p className="text-[10px] text-brand-primary font-black uppercase tracking-[0.3em] mb-4 flex items-center gap-2">
                    <Sparkles size={12} /> Location
                  </p>
                  <h3 className="text-3xl font-black mb-1 uppercase tracking-tighter text-foreground group-hover:text-brand-primary transition-colors">Prizren</h3>
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest italic">Republic of Kosovo</p>
                </div>
                <div className="group">
                  <p className="text-[10px] text-brand-accent font-black uppercase tracking-[0.3em] mb-4 flex items-center gap-2">
                    <Sparkles size={12} /> Official Site
                  </p>
                  <a href="https://ergys.dev" target="_blank" rel="noreferrer" className="block">
                    <h3 className="text-3xl font-black mb-1 uppercase tracking-tighter flex items-center gap-3 text-foreground group-hover:text-brand-accent transition-colors">
                      ergys.dev <ArrowUpRight size={24} className="text-muted-foreground group-hover:text-brand-accent transition-all group-hover:-translate-y-1 group-hover:translate-x-1" />
                    </h3>
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest italic">Business Portfolio</p>
                  </a>
                </div>
              </div>
            </div>

            <div className="relative group lg:mt-0">
              <div className="absolute -inset-4 border border-brand-primary/20 rounded-[3rem] -z-10 group-hover:scale-105 group-hover:border-brand-primary/40 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-primary/20 via-brand-secondary/10 to-transparent rounded-[2.5rem] -z-10" />
              
              <div className="relative rounded-[2.5rem] overflow-hidden border border-border shadow-2xl group">
                 <ImageWithFallback 
                    src="https://images.unsplash.com/photo-1766051665906-0fd23cbd276f?crop=entropy&cs=tinysrgb&fit=max&fm=webp&q=75&w=800&auto=format"
                    alt="Modern tech workspace with neon lighting"
                    className="w-full aspect-square md:aspect-video lg:aspect-square object-cover group-hover:scale-110 transition-transform duration-1000 opacity-60 group-hover:opacity-100"
                 />
                 <div className="absolute inset-0 bg-background/20 group-hover:bg-transparent transition-colors duration-500" />
                 
                 <div className="absolute bottom-10 left-10 right-10 p-8 bg-background/80 backdrop-blur-xl border border-border rounded-3xl translate-y-full group-hover:translate-y-0 transition-transform duration-700">
                    <p className="text-xs font-black uppercase tracking-[0.2em] text-brand-primary mb-2">My Workspace</p>
                    <p className="text-lg font-black text-foreground leading-tight uppercase tracking-tight italic">Where code meets <span className="text-brand-secondary">creativity</span>.</p>
                 </div>
              </div>
            </div>
          </div>
        </Section>

        <Section id="why-choose-me" title="Why Choose Me" subtitle="Delivering value through technical excellence and strategic thinking.">
          <WhyChooseUs />
        </Section>

        <Section id="experience" title="Professional Path" subtitle="A timeline of my growth in the tech industry.">
          <Experience />
        </Section>

        <Section 
          id="projects" 
          title="Solutions Delivered" 
          subtitle="A selection of business platforms, admin tools, and web applications I've engineered."
          dark
        >
          <Projects />
        </Section>

        <Section id="testimonials" title="Client Voices" subtitle="What business owners and partners say about our collaboration.">
          <Testimonials />
        </Section>

        <Section 
          id="skills" 
          title="Digital Skills" 
          subtitle="Frontend, Backend, Design, and AI-assisted development toolkits."
          dark
        >
          <Skills />
        </Section>

        <Section id="newsletter" title="Stay Informed" subtitle="Join my network for exclusive tech insights and project updates.">
          <Newsletter />
        </Section>

        <Section id="faq" title="Common Questions" subtitle="Everything you need to know about working with me.">
          <FAQ />
        </Section>

        <Section id="blog" title="Latest Insights" subtitle="Thoughts on development, design, and building better digital experiences.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {posts.slice(0, 3).map((post, index) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="group block h-full bg-card backdrop-blur-md border border-border rounded-[2rem] overflow-hidden hover:border-brand-primary/50 transition-all duration-500"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                  </div>
                  <div className="p-8">
                    <span className="text-[10px] font-black uppercase tracking-widest text-brand-primary mb-4 block">{post.category}</span>
                    <h3 className="text-xl font-black mb-4 text-foreground group-hover:text-brand-primary transition-colors uppercase tracking-tight leading-tight">
                      {post.title}
                    </h3>
                    <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1"><Calendar size={12} /> {post.date}</span>
                        <span className="flex items-center gap-1"><Clock size={12} /> {post.readTime}</span>
                      </div>
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform text-brand-primary" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors group"
            >
              View All Articles <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Section>

        <Section 
          id="contact" 
          title="Contact" 
          subtitle="Ready to discuss your next technical challenge?"
          dark
        >
          <div className="space-y-20">
            <ContactForm />
            <div className="pt-20 border-t border-border relative">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-brand-primary/5 blur-[120px] rounded-full pointer-events-none" />
              
              <div className="max-w-3xl mx-auto text-center mb-12 relative z-10">
                <h3 className="text-2xl font-black uppercase tracking-tight text-foreground mb-4 italic">Visit My Office</h3>
                <p className="text-muted-foreground font-medium">Located in the heart of Prizren, Kosovo.</p>
              </div>
              <Map />
            </div>
          </div>
        </Section>
      </motion.div>
    </>
  );
}